import React, { useEffect, useRef, useState } from 'react';
import Fetch from '../../../../core/util/core/Fetch';
import config from '../../../config/config';

import SalesMapHeader from './components/SalesMapHeader';
import SalesMapStats from './components/SalesMapStats';
import SalesMapLegend from './components/SalesMapLegend';
import SalesMapGrid from './components/SalesMapGrid';
import { exportSalesMapPDF } from './utils/exportPDF';
import { groupUnits, sortBlockKeys, sortFloorKeys } from './utils/salesMapUtils';

import './SalesMap.css';

function SalesMap({ projectId, project: projectProp, onClose }) {
    const gridRef = useRef(null);

    const [units, setUnits] = useState([]);
    const [projectName, setProjectName] = useState(projectProp?.project_name || '');
    const [companyName, setCompanyName] = useState('');
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const [grouped, setGrouped] = useState({});
    const [allFloors, setAllFloors] = useState([]);
    const [blocks, setBlocks] = useState([]);

    const endpointBackend = config.endpoints.backend;
    const resolvedProjectId = projectId || projectProp?.id;

    // ── Carrega dados do projeto e unidades ──────────────────
    useEffect(() => {
        const loadUnits = async () => {
            setLoading(true);
            try {
                if (projectId && !projectProp) {
                    const projectData = await Fetch.post(
                        `${endpointBackend}gci/projects/getById`,
                        { id: projectId }
                    );
                    if (projectData?.project_name) setProjectName(projectData.project_name);
                    if (projectData?.company) setCompanyName(projectData.company);
                }

                const rows = await Fetch.post(`${endpointBackend}gci/units/getall`, {
                    filter: { gci_project_id: resolvedProjectId },
                    filters: { gci_project_id: resolvedProjectId },
                    order: [['block', 'ASC'], ['floor', 'ASC'], ['number', 'ASC']],
                });

                const data = rows || [];
                setUnits(data);

                const g = groupUnits(data);
                setGrouped(g);

                const bKeys = sortBlockKeys(Object.keys(g));
                setBlocks(bKeys);

                const floorSet = new Set();
                Object.values(g).forEach(floorMap =>
                    Object.keys(floorMap).forEach(f => floorSet.add(f))
                );
                setAllFloors(sortFloorKeys([...floorSet]));
            } catch (err) {
                console.error('Erro ao carregar unidades:', err);
            } finally {
                setLoading(false);
            }
        };

        if (resolvedProjectId) loadUnits();
    }, [resolvedProjectId]);

    // ── Estatísticas ─────────────────────────────────────────
    const stats = units.reduce((acc, unit) => {
        acc[unit.status] = (acc[unit.status] || 0) + 1;
        return acc;
    }, {});

    const hasBlocks = blocks.some(b => b !== '—');

    // ── Export PDF ───────────────────────────────────────────
    const handleExportPDF = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            await exportSalesMapPDF(gridRef.current, companyName, projectName);
        } catch (err) {
            console.error('Erro ao exportar PDF:', err);
        } finally {
            setExporting(false);
        }
    };

    // ── Render ───────────────────────────────────────────────
    return (
        <div className="sales-map-page">
            <SalesMapHeader
                projectName={projectName}
                onClose={onClose}
                onExportPDF={handleExportPDF}
                exporting={exporting}
            />

            <SalesMapStats stats={stats} totalUnits={units.length} />

            <SalesMapLegend />

            {loading ? (
                <div className="sales-map-loading">
                    <div className="spinner-border text-primary" role="status" />
                    <span>Carregando unidades...</span>
                </div>
            ) : units.length === 0 ? (
                <div className="sales-map-empty">
                    <i className="fas fa-building" />
                    <p>Nenhuma unidade cadastrada para este projeto.</p>
                </div>
            ) : (
                <SalesMapGrid
                    gridRef={gridRef}
                    blocks={blocks}
                    allFloors={allFloors}
                    grouped={grouped}
                    hasBlocks={hasBlocks}
                />
            )}
        </div>
    );
}

export default SalesMap;
